import React, { useState, useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shared/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shared/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/shared/tooltip";
import { supabase } from '@/lib/supabase';
import { cn, formatPlaceName } from '@/lib/utils';
import { Place } from '@/types';

interface AddPeerButtonProps {
  onPeerSelected: (peer: Place) => void;
  selectedPlace: Place | null;
}

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

const AddPeerButton: React.FC<AddPeerButtonProps> = ({ onPeerSelected, selectedPlace }) => {
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [placeOptions, setPlaceOptions] = useState<Place[]>([]);
  const [loadingPlaceOptions, setLoadingPlaceOptions] = useState<boolean>(false);
  const [placeOptionsError, setPlaceOptionsError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedPlace) {
      event.preventDefault();
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 820);
    }
  };

  useEffect(() => {
    if (!comboboxOpen) {
      setPlaceOptions([]);
      setLoadingPlaceOptions(false);
      return;
    }

    if (searchInputValue.trim().length < MIN_SEARCH_LENGTH) {
      setPlaceOptions([]);
      setLoadingPlaceOptions(false);
      setPlaceOptionsError(null);
      return;
    }

    setLoadingPlaceOptions(true);
    setPlaceOptionsError(null);

    const handler = setTimeout(async () => {
      try {
        const searchTerm = searchInputValue.trim();
        const { data, error } = await supabase
          .from('acs_bps_metrics_statics')
          .select('Place, place_fips')
          .ilike('Place', `%${searchTerm}%`)
          .order('Place', { ascending: true })
          .limit(50);

        if (error) {
          throw error;
        }

        if (data) {
          const uniquePlaces = Array.from(new Map(data.map(item =>
            [item.place_fips, { name: item.Place, place_fips: String(item.place_fips) }]
          )).values());
          setPlaceOptions(uniquePlaces);
        } else {
          setPlaceOptions([]);
        }
      } catch (err: any) {
        console.error('Error fetching filtered place options:', err);
        setPlaceOptionsError('Failed to load suggestions.');
        setPlaceOptions([]);
      } finally {
        setLoadingPlaceOptions(false);
      }
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputValue, comboboxOpen]);

  const trigger = (
    <button
      onClick={handleButtonClick}
      className={cn(
        "flex items-center justify-center w-full h-full p-2 border rounded-md border-dashed text-gray-400 hover:text-gray-600 hover:border-gray-400",
        isShaking && 'animate-shake'
      )}
    >
      {selectedPlace ? "+ Add a Peer" : "Waiting for Selection..."}
    </button>
  );

  if (!selectedPlace) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>
            <p>Select a main jurisdiction above first!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-white opacity-100 border-2 border-gray-300" side="bottom" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search for a jurisdiction..."
            value={searchInputValue}
            onValueChange={setSearchInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {loadingPlaceOptions
                ? "Loading..."
                : placeOptionsError
                  ? placeOptionsError
                  : searchInputValue.trim().length > 0 && searchInputValue.trim().length < MIN_SEARCH_LENGTH
                    ? `Type at least ${MIN_SEARCH_LENGTH} characters.`
                    : searchInputValue.trim().length >= MIN_SEARCH_LENGTH && placeOptions.length === 0 && !placeOptionsError
                      ? `No results found for \"${searchInputValue}\".`
                      : "Start typing to search for a jurisdiction."}
            </CommandEmpty>
            <CommandGroup>
              {placeOptions.map((option) => (
                <CommandItem
                  key={option.place_fips}
                  value={option.name}
                  onSelect={(currentValue) => {
                    const newSelected = placeOptions.find(opt => opt.name === currentValue);
                    if (newSelected) {
                      onPeerSelected(newSelected);
                    }
                    setComboboxOpen(false);
                    setSearchInputValue('');
                  }}
                >
                  {formatPlaceName(option.name)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AddPeerButton; 